import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Chip, 
  Card, 
  CardContent,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon,
  ShowChart as ShowChartIcon,
  Delete as DeleteIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { getTrade, deleteTrade } from './tradesSlice';
import { loadMarketData } from '../markets/marketsSlice';
import { fetchTradeState } from './executionsSlice';
import DWLFMarketChart from '../../components/DWLFMarketChart';
import { calculateActualPnL } from '../../utils/tradeCalculations';
import ExecutionsQuickActions from './ExecutionsQuickActions';
import ExecutionsTimelineSimple from './ExecutionsTimelineSimple';
import TradeNotesSection from './TradeNotesSection';
import { getSkipReasonLabels, getSkipVerdict } from './reviewHelpers';
import { getEventTypeLabel } from '../../constants/eventTypes';
import DWLFLoader from '../../components/DWLFLoader';
import useStaleDataRefresh from '../../hooks/useStaleDataRefresh';
import apiClient from '../../api/apiClient';

const formatSymbolForRoute = (symbol) => (symbol ? symbol.replace(/\//g, '-') : '');

const TradeDetailPage = () => {
  const { tradeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { current, recentActions, loading, error } = useSelector((s) => s.trades);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Market data for chart
  const marketData = useSelector((state) => {
    if (!current?.assetSymbol) return null;
    const formattedSymbol = current.assetSymbol.replace('/', '-');
    const data = state.markets.marketData[formattedSymbol] || null;
    return data;
  });
  
  const marketDataLoading = useSelector((state) => {
    if (!current?.assetSymbol) return false;
    const formattedSymbol = current.assetSymbol.replace('/', '-');
    const hasData = state.markets.marketData?.[formattedSymbol]?.candles?.length > 0;
    const isLoading = state.markets.status === 'loading';
    // Only show loading spinner on initial load, not background refresh
    return isLoading && !hasData;
  });

  // Auto-refresh stale chart data (>1 hour old)
  const formattedSymbolForRefresh = current?.assetSymbol?.replace('/', '-') || null;
  useStaleDataRefresh(formattedSymbolForRefresh, 'daily');

  // Get trade state for current stop loss and take profit
  const tradeState = useSelector((state) => {
    if (!current?.tradeId) return null;
    return state.executions.tradeStates[current.tradeId] || null;
  });
  


  // Counterfactual outcome for SKIPPED trades — powers the ghost-trade view
  // (verdict banner + frozen levels/markers on the chart).
  const [skipOutcome, setSkipOutcome] = useState(null);

  useEffect(() => {
    dispatch(getTrade(tradeId));
  }, [dispatch, tradeId]);

  useEffect(() => {
    if (current?.status !== 'skipped' || current?.tradeId !== tradeId) {
      setSkipOutcome(null);
      return undefined;
    }
    let cancelled = false;
    apiClient
      .get('/trades/skip-outcomes')
      .then((res) => {
        if (cancelled) return;
        const match = (res.data?.outcomes || []).find((o) => o.tradeId === tradeId);
        setSkipOutcome(match || null);
      })
      .catch(() => {
        // The ghost view is additive — the page works without it.
      });
    return () => { cancelled = true; };
  }, [current?.status, current?.tradeId, tradeId]);

  // Fetch market data when trade loads
  useEffect(() => {
    if (current?.assetSymbol) {
      const formattedSymbol = current.assetSymbol.replace('/', '-');
      dispatch(loadMarketData(formattedSymbol));
    }
  }, [dispatch, current?.assetSymbol]);

  // Fetch trade state for executions system
  useEffect(() => {
    if (current?.tradeId) {
      // For open trades, include current market price for unrealized P&L calculation
      const currentPrice = marketData?.candles?.[marketData.candles.length - 1]?.close;
      dispatch(fetchTradeState({ 
        tradeId: current.tradeId,
        mark: current.status === 'open' ? currentPrice : null
      }));
    }
  }, [dispatch, current?.tradeId, current?.status, marketData?.candles]);

  // Debug trade data
  useEffect(() => {
    if (current) {
      // Debug data removed for production
    }
  }, [current]);

  const formatDate = (dateString) => {
    // Render em-dash for missing dates (planned/skipped trades have no
    // entryAt; open trades have no exitAt) — otherwise new Date(null) renders
    // the raw "Invalid Date" string.
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDirectionColor = (direction) => {
    return direction === 'long' ? 'success' : 'error';
  };

  const getStatusColor = (status) => {
    return status === 'open' ? 'warning' : 'success';
  };





  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteTrade(tradeId)).unwrap();
      navigate('/trades');
    } catch (error) {
      console.error('Failed to delete trade:', error);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>Error loading trade: {error}</Alert>
      </Box>
    );
  }

  if (!current) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ borderRadius: 0 }}>Trade not found</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <IconButton
          onClick={() => navigate('/trades')}
          sx={{ 
            mr: 1, 
            borderRadius: 0,
            border: '1px solid var(--dwlf-ink-10)',
            '&:hover': {
              borderColor: 'var(--dwlf-accent)',
              backgroundColor: 'var(--dwlf-accent-05)',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box
          display="flex"
          alignItems="center"
          flexGrow={1}
          gap={1.5}
          flexWrap="wrap"
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            {current.assetSymbol && (
              <Tooltip title="View Market">
                <IconButton
                  component={RouterLink}
                  to={`/markets/${formatSymbolForRoute(current.assetSymbol)}`}
                  size="small"
                  sx={{
                    borderRadius: 0,
                    border: '1px solid var(--dwlf-accent-30)',
                    color: 'var(--dwlf-accent)',
                    '&:hover': {
                      backgroundColor: 'var(--dwlf-accent-10)',
                      borderColor: 'var(--dwlf-accent)',
                    },
                  }}
                  aria-label="View market details"
                >
                  <ShowChartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Typography 
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                fontWeight: 600,
                letterSpacing: '-0.02em',
              }}
            >
              {current.assetSymbol}
            </Typography>
            <Chip 
              label={current.direction.toUpperCase()} 
              color={getDirectionColor(current.direction)}
              size="small"
              sx={{ 
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: 0,
              }}
            />
            <Chip
              label={current.status.toUpperCase()}
              color={getStatusColor(current.status)}
              size="small"
              sx={{ 
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: 0,
              }}
            />
            {current.isPaperTrade && (
              <Chip
                label="PAPER"
                color="info"
                size="small"
                sx={{ 
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  borderRadius: 0,
                }}
              />
            )}
          </Box>
          <IconButton
            color="error"
            size="small"
            onClick={handleDeleteClick}
            sx={{ 
              ml: 'auto',
              borderRadius: 0,
              border: '1px solid var(--dwlf-down-30)',
              '&:hover': {
                backgroundColor: 'var(--dwlf-down-10)',
                borderColor: 'var(--dwlf-down)',
              },
            }}
          >
            <DeleteIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Skip Reason Section — only for skipped trades. The reasons/note are
          persisted on the trade row (skipReasons[]/skipReason/skipNote/skippedAt)
          but were previously never surfaced, leaving the skip journal write-only. */}
      {current.status === 'skipped' && (() => {
        const skipLabels = getSkipReasonLabels(current.skipReasons, current.skipReason);
        return (
          <Box mb={3}>
            <Paper sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'rgba(255, 167, 38, 0.4)',
              borderRadius: 2,
              backgroundColor: 'rgba(255, 167, 38, 0.06)',
            }}>
              <Box display="flex" alignItems="center" mb={1.5}>
                <BlockIcon sx={{ mr: 1, color: 'warning.main', fontSize: '1.1rem' }} />
                <Typography variant="h6" fontWeight={600}>
                  Why this trade was skipped
                </Typography>
              </Box>

              {skipLabels.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={current.skipNote ? 1.5 : 0}>
                  {skipLabels.map((label, i) => (
                    <Chip
                      key={i}
                      label={label}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ borderRadius: 0, fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}

              {current.skipNote && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {current.skipNote}
                </Typography>
              )}

              {skipLabels.length === 0 && !current.skipNote && (
                <Typography variant="body2" color="text.secondary">
                  No reason was recorded for this skip.
                </Typography>
              )}

              {current.skippedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Skipped {new Date(current.skippedAt).toLocaleString()}
                </Typography>
              )}
            </Paper>

            {/* Ghost-trade verdict: the mechanical replay of the skipped plan
                (decision-time-frozen entry/stop/target vs what price did). */}
            {skipOutcome && (() => {
              const verdict = getSkipVerdict(skipOutcome);
              const toneColor = verdict.tone === 'good' ? '#4caf50' : verdict.tone === 'bad' ? '#ef4444' : 'var(--dwlf-ink-50)';
              return (
                <Paper sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: verdict.tone === 'good' ? 'rgba(76, 175, 80, 0.4)' : verdict.tone === 'bad' ? 'rgba(239, 68, 68, 0.4)' : 'var(--dwlf-ink-15)',
                  borderRadius: 2,
                  backgroundColor: verdict.tone === 'good' ? 'rgba(76, 175, 80, 0.06)' : verdict.tone === 'bad' ? 'rgba(239, 68, 68, 0.06)' : 'transparent',
                }}>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5} mb={1}>
                    <Typography variant="h6" fontWeight={600}>
                      What would have happened
                    </Typography>
                    <Chip label={verdict.label} size="small" color={verdict.color} sx={{ fontWeight: 600 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: toneColor }}>
                      {verdict.savedRText}
                    </Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={3}>
                    <Typography variant="body2" color="text.secondary">
                      Signal {skipOutcome.signalDate}
                    </Typography>
                    {skipOutcome.exitDate && (
                      <Typography variant="body2" color="text.secondary">
                        Resolved {skipOutcome.exitDate}
                      </Typography>
                    )}
                    {skipOutcome.mfeAt && (
                      <Typography variant="body2" sx={{ color: '#4caf50' }}>
                        Peak +{Number(skipOutcome.mfeR).toFixed(2)}R on {skipOutcome.mfeAt}
                      </Typography>
                    )}
                    {skipOutcome.maeAt && (
                      <Typography variant="body2" sx={{ color: '#ef4444' }}>
                        Worst {Number(skipOutcome.maeR).toFixed(2)}R on {skipOutcome.maeAt}
                      </Typography>
                    )}
                    {skipOutcome.status === 'running' && skipOutcome.currentPrice != null && (
                      <Typography variant="body2" color="text.secondary">
                        Marked at {Number(skipOutcome.currentPrice).toFixed(2)} ({skipOutcome.lastCandleDate})
                      </Typography>
                    )}
                  </Box>
                  {skipOutcome.partialCoverage && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                      Partial candle coverage — the replay starts after the signal date; an early stop or target hit could be missed.
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Mechanical replay of the planned levels, plus the strategy&apos;s own exit graph — no
                    discretionary management. Stopped outcomes cap at −1R; exited outcomes close at the
                    exit bar&apos;s close, so they carry either sign.
                  </Typography>
                </Paper>
              );
            })()}
          </Box>
        );
      })()}

      {/* Market Chart Section */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <ShowChartIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Market Chart
          </Typography>
        </Box>
        <Paper sx={{ 
          p: 2, 
          border: '1px solid', 
          borderColor: 'var(--dwlf-ink-10)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '400px'
        }}>
                      <Box sx={{ 
              flex: 1,
              minHeight: 0,
              position: 'relative'
            }}>
            {marketDataLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: 'text.secondary'
              }}>
                <DWLFLoader size="medium" />
                <Typography variant="h6" sx={{ ml: 2 }}>
                  Loading chart data...
                </Typography>
              </Box>
            ) : marketData?.candles && marketData.candles.length > 0 ? (
              (() => {
                return (
                                <DWLFMarketChart 
                isMobile={isMobile}
                title={`${current.assetSymbol} - Trade Chart`}
                symbol={current.assetSymbol.replace('/', '-')} 
                marketData={marketData.candles} 
                supportResistance={null} 
                trendlines={[]} 
                weeklyTrendlines={[]}
                swingPoints={[]}
                weeklySwingPoints={[]}
                alerts={[]}
                fibLevels={[]}
                highlightedPatterns={[]}
                tradeSignals={[]}
                tradeSignalType="system"
                onTradeSignalTypeChange={() => {}}
                availableStrategies={[]}
                selectedStrategy="all"
                onStrategyChange={() => {}}
                // Trade-specific overlays. Skipped trades render the GHOST
                // view: the frozen plan as a position zone (risk/reward bands
                // from signal date to resolution) plus peak/trough/verdict
                // markers from the counterfactual replay.
                tradeOverlays={current.status === 'skipped' && skipOutcome ? (() => {
                  const o = skipOutcome;
                  const dir = o.direction === 'short' ? -1 : 1;
                  const risk = Number(o.riskPerShare) || Math.abs(o.entryPrice - o.initialStop);
                  const ghostEnd = o.exitDate || o.lastCandleDate;
                  const resolutionPrice = o.status === 'would_be_stopped' ? o.initialStop
                    : o.status === 'would_hit_target' ? o.initialTakeProfit
                    // A strategy exit closes at the exit BAR'S CLOSE, which the
                    // payload doesn't carry directly — derive it from the R it
                    // booked. Falling through to currentPrice drew the verdict
                    // marker on the correct exit date at TODAY'S price: right
                    // date, wrong height, which reads as an exit that never
                    // happened at that level.
                    : o.status === 'would_exit_signal'
                      ? Number(o.entryPrice) + dir * Number(o.counterfactualR) * risk
                    : o.currentPrice;
                  const overlays = [{
                    type: 'position',
                    key: 'ghost-trade',
                    startDate: o.signalDate,
                    endDate: ghostEnd,
                    entryPrice: o.entryPrice,
                    stopPrice: o.initialStop,
                    targetPrice: o.initialTakeProfit ?? undefined,
                    riskColor: 'rgba(239, 68, 68, 0.10)',
                    rewardColor: 'rgba(76, 175, 80, 0.10)',
                    messages: [{ date: o.signalDate, price: o.entryPrice, text: 'Skipped here' }],
                  }];
                  if (o.mfeAt && Number(o.mfeR) > 0.05) {
                    overlays.push({
                      type: 'marker',
                      key: 'ghost-peak',
                      color: '#4caf50',
                      shape: 'circle',
                      points: [{
                        date: o.mfeAt,
                        price: o.entryPrice + dir * Number(o.mfeR) * risk,
                        text: `Peak +${Number(o.mfeR).toFixed(1)}R`,
                      }],
                    });
                  }
                  if (o.exitDate && Number.isFinite(Number(resolutionPrice))) {
                    overlays.push({
                      type: 'marker',
                      key: 'ghost-verdict',
                      color: o.status === 'would_be_stopped' ? '#ef4444' : '#fbbf24',
                      shape: 'square',
                      points: [{
                        date: o.exitDate,
                        price: Number(resolutionPrice),
                        text: o.status === 'would_be_stopped' ? 'Stopped −1R'
                          : o.status === 'would_hit_target' ? `Target +${Number(o.counterfactualR).toFixed(1)}R`
                          : `Exit ${Number(o.counterfactualR) >= 0 ? '+' : ''}${Number(o.counterfactualR).toFixed(1)}R`,
                      }],
                    });
                  }
                  return overlays;
                })() : {
                  entryPrice: current.entryPrice,
                  entryDate: current.entryAt,
                  exitPrice: current.exitPrice,
                  exitDate: current.exitAt,
                  // Use current stop loss and take profit from trade state, fallback to initial values
                  initialStop: tradeState?.activeStop || current.initialStop,
                  initialTakeProfit: tradeState?.activeTakeProfit || current.initialTakeProfit,
                  direction: current.direction,
                  linkedEvents: current.linkedEvents || [],
                  recentActions: recentActions,
                  // Add execution markers for position changes
                  executions: tradeState?.executions || []
                }}

              />
                );
              })()
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: 'text.secondary'
              }}>
                <Typography variant="h6">
                  No chart data available
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '420px 1fr' },
          gap: 3
        }}
      >
        {/* Trade Summary Card */}
        <Box>
          <Card sx={{ height: 'fit-content', width: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Trade Summary
              </Typography>
              
              {/* Entry Price / Average Entry */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  {current.status === 'closed' ? 'Average Entry' : 'Entry Price'}
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  ${(() => {
                    // For closed trades, try to calculate average from executions
                    if (current.status === 'closed' && tradeState?.executions?.length > 0) {
                      const entryExecutions = tradeState.executions.filter(exec => 
                        ['entry', 'add'].includes(exec.type) && exec.qty > 0 && exec.price > 0
                      );
                      if (entryExecutions.length > 0) {
                        const totalCost = entryExecutions.reduce((sum, exec) => sum + (exec.qty * exec.price), 0);
                        const totalShares = entryExecutions.reduce((sum, exec) => sum + exec.qty, 0);
                        if (totalShares > 0) {
                          return (totalCost / totalShares).toFixed(2);
                        }
                      }
                    }
                    // Fallback to original entry price
                    return current.entryPrice?.toFixed(2) || '0.00';
                  })()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(current.entryAt)}
                </Typography>
                {/* Show original entry price for closed trades if different */}
                {current.status === 'closed' && tradeState?.executions?.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Originally: ${current.entryPrice?.toFixed(2)}
                  </Typography>
                )}
              </Box>

              {/* Position Size / Total Shares */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  {current.status === 'closed' ? 'Total Shares Bought' : 'Position Size'}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {(() => {
                    // For closed trades, calculate total shares bought
                    if (current.status === 'closed' && tradeState?.executions?.length > 0) {
                      const totalShares = tradeState.executions
                        .filter(exec => ['entry', 'add'].includes(exec.type) && exec.qty > 0)
                        .reduce((sum, exec) => sum + exec.qty, 0);
                      if (totalShares > 0) {
                        return `${totalShares} shares`;
                      }
                    }
                    // Fallback to original position size
                    return `${current.positionSize || 0} shares`;
                  })()}
                </Typography>
                {/* Show original position size for closed trades if different */}
                {current.status === 'closed' && tradeState?.executions?.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Originally: {current.positionSize} shares
                  </Typography>
                )}
              </Box>

              {/* Stop Loss */}
              {(tradeState?.activeStop || current.initialStop) && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {tradeState?.activeStop ? 'Current Stop Loss' : 'Initial Stop Loss'}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="error.main">
                    ${(tradeState?.activeStop || current.initialStop)?.toFixed(2)}
                  </Typography>
                  {tradeState?.activeStop && tradeState.activeStop !== current.initialStop && (
                    <Typography variant="caption" color="text.secondary">
                      Originally: ${current.initialStop?.toFixed(2)}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Take Profit */}
              {(tradeState?.activeTakeProfit || current.initialTakeProfit) && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {tradeState?.activeTakeProfit ? 'Current Take Profit' : 'Initial Take Profit'}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    ${(tradeState?.activeTakeProfit || current.initialTakeProfit)?.toFixed(2)}
                  </Typography>
                  {tradeState?.activeTakeProfit && tradeState.activeTakeProfit !== current.initialTakeProfit && (
                    <Typography variant="caption" color="text.secondary">
                      Originally: ${current.initialTakeProfit?.toFixed(2)}
                    </Typography>
                  )}
                </Box>
              )}

               {current.reasonText && (
                 <Box mb={2}>
                   <Typography variant="body2" color="text.secondary">
                     Trade Reason
                   </Typography>
                   <Typography variant="body1" sx={{ 
                     backgroundColor: 'background.surface', 
                     p: 1.5, 
                     borderRadius: 1,
                     border: '1px solid',
                     borderColor: 'divider'
                   }}>
                     {current.reasonText}
                   </Typography>
                 </Box>
               )}

               {/* Trade Plan Information */}
               {current.plan && (
                 <Box mb={2}>
                   <Typography variant="body2" color="text.secondary">
                     Trade Plan
                   </Typography>
                   <Box sx={{ 
                     backgroundColor: 'background.surface', 
                     p: 1.5, 
                     borderRadius: 1,
                     border: '1px solid',
                     borderColor: 'divider'
                   }}>
                     <Typography variant="body1" fontWeight={600} color="primary.main" gutterBottom>
                       {current.plan.name}
                     </Typography>
                     {current.plan.description && (
                       <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                         {current.plan.description}
                       </Typography>
                     )}
                     <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                       <Chip 
                         label={current.plan.category} 
                         size="small" 
                         variant="outlined"
                         color="primary"
                       />
                       {current.plan.entryCriteria && (
                         <Chip 
                           label="Entry Criteria" 
                           size="small" 
                           variant="outlined"
                           color="secondary"
                         />
                       )}
                       {current.plan.managementRules && current.plan.managementRules.length > 0 && (
                         <Chip 
                           label={`${current.plan.managementRules.length} Rules`} 
                           size="small" 
                           variant="outlined"
                           color="info"
                         />
                       )}
                     </Box>
                     {current.plan.entryCriteria && (
                       <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                         <strong>Entry:</strong> {current.plan.entryCriteria}
                       </Typography>
                     )}
                   </Box>
                 </Box>
               )}

               {current.linkedEvents && current.linkedEvents.length > 0 && (
                 <Box mb={2}>
                   <Typography variant="body2" color="text.secondary">
                     Linked Events ({current.linkedEvents.length})
                   </Typography>
                   <Box mt={1}>
                     {current.linkedEvents.map((eventId, index) => {
                       // Parse event ID to extract information
                       const parts = eventId.split('_');
                       const symbol = parts[0];
                       const eventType = parts.slice(1, -1).join('_'); // Handle multi-part event types
                       const date = parts[parts.length - 1];
                       
                       const getEventDescription = (type) => getEventTypeLabel(type);

                       const getEventIcon = (type) => {
                         // Bullish events
                         if (type === 'higher_high' || type === 'higher_low' || 
                             type === 'swing_high_formed' || type === 'swing_high_break' || type === 'ema_bullish_alignment' || 
                             type === 'ema_bullish_cloud_hit' || type === 'trendline_break_bullish' ||
                             type === 'trendline_breach_bullish') {
                           return <TrendingUpIcon color="success" fontSize="small" />;
                         }
                         // Bearish events
                         if (type === 'lower_low' || type === 'lower_high' || 
                             type === 'swing_low_formed' || type === 'swing_low_break' || type === 'ema_bearish_alignment' || 
                             type === 'ema_bearish_cloud_hit' || type === 'trendline_break_bearish' ||
                             type === 'trendline_breach_bearish') {
                           return <TrendingDownIcon color="error" fontSize="small" />;
                         }
                         // Neutral events
                         if (type === 'trendline_break' || type === 'trendline_breach' ||
                             type === 'ema_bullish_unalignment' || 
                             type === 'ema_bearish_unalignment') {
                           return <TimelineIcon color="warning" fontSize="small" />;
                         }
                         return <TimelineIcon fontSize="small" />;
                       };

                       const formatEventDate = (dateStr) => {
                         try {
                           return new Date(dateStr).toLocaleDateString('en-US', {
                             month: 'short',
                             day: 'numeric',
                             year: 'numeric'
                           });
                         } catch {
                           return dateStr;
                         }
                       };

                       return (
                         <Box
                           key={index}
                           sx={{
                             display: 'flex',
                             alignItems: 'center',
                             gap: 1,
                             p: 1,
                             mb: 1,
                             backgroundColor: 'background.surface',
                             borderRadius: 1,
                             border: '1px solid',
                             borderColor: 'divider'
                           }}
                         >
                           {getEventIcon(eventType)}
                           <Box flex={1}>
                             <Typography variant="body2" fontWeight={600}>
                               {symbol}
                             </Typography>
                             <Typography variant="caption" color="text.secondary">
                               {getEventDescription(eventType)} • {formatEventDate(date)}
                             </Typography>
                           </Box>
                         </Box>
                       );
                     })}
                   </Box>
                 </Box>
               )}

              {/* Exit Price - Only for closed trades */}
              {current.status === 'closed' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Exit Price
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      ${current.exitPrice?.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(current.exitAt)}
                    </Typography>
                  </Box>
                </>
              )}

              {/* P&L Section - For both open and closed trades */}
              {(current.status === 'closed' || (current.status === 'open' && tradeState?.positionState)) && (
                <>
                  {current.status === 'closed' && <Divider sx={{ my: 2 }} />}
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      {current.status === 'closed' ? 'P&L' : 'Unrealized P&L'}
                    </Typography>
                    {(() => {
                      let pnl, pnlPercentage;
                      
                      if (current.status === 'closed') {
                        // For closed trades, use realized P&L
                        const executionPnL = tradeState?.positionState?.realizedPnl;
                        pnl = executionPnL !== undefined ? executionPnL : calculateActualPnL(current);
                        pnlPercentage = current.pnlPct || 0;
                      } else {
                        // For open trades, use unrealized P&L
                        pnl = tradeState?.positionState?.unrealizedPnl || 0;
                        const avgEntry = tradeState?.positionState?.avgEntry || current.entryPrice;
                        const positionSize = tradeState?.positionState?.qty || current.positionSize;
                        if (avgEntry && positionSize) {
                          const totalCost = avgEntry * positionSize;
                          pnlPercentage = totalCost > 0 ? (pnl / totalCost) * 100 : 0;
                        } else {
                          pnlPercentage = 0;
                        }
                      }
                      
                      return (
                        <>
                          <Typography 
                            variant="h5" 
                            fontWeight={700} 
                            color={pnl >= 0 ? 'success.main' : 'error.main'}
                          >
                            {pnl >= 0 ? '+' : ''}${pnl?.toFixed(2)}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color={pnlPercentage >= 0 ? 'success.main' : 'error.main'}
                          >
                            {pnlPercentage >= 0 ? '+' : ''}{pnlPercentage?.toFixed(2)}%
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>

                  {/* R-Multiple - Only for closed trades */}
                  {current.status === 'closed' && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        R-Multiple
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {(tradeState?.positionState?.realizedR || current.rMultiple)?.toFixed(2)}R
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Executions Quick Actions */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Executions & Quick Actions
              </Typography>
              
              <ExecutionsQuickActions 
                tradeId={current.tradeId} 
                currentPrice={marketData?.candles?.[marketData.candles.length - 1]?.close}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <TradeNotesSection
                tradeId={current.tradeId}
                linkedEvents={current.linkedEvents || []}
                symbol={current.assetSymbol}
                entryAt={current.entryAt}
              />
            </CardContent>
          </Card>

          {/* Executions Timeline */}
          <Card>
            <CardContent>
              <ExecutionsTimelineSimple tradeId={current.tradeId} />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Trade
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this trade? This action will permanently delete the trade and all associated executions and adjustments. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Trade'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TradeDetailPage;
